/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Container, FormField, Grid, Header, Hotspot, SpaceBetween, Textarea } from '@amzn/awsui-components-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChallengeLearningOutcomeFields,
  ChallengePropAction,
  SUMMARY_MAX_LENGTH,
  SUMMARY_MIN_LENGTH,
  LEARNING_OUTCOME_CONTENT_MAX_LENGTH,
  useCreateChallenge,
} from '../../../store/create-challenge.context';
import { ChallengeLearningOutcome } from '../../../types/Challenge';
import { i18nKeys } from '../../../utils/i18n.utils';
import { ChallengeHotspot } from '../challengesCommon/ChallengeHotspots';
import MarkDownPreviewEditor from '../../common/MarkdownPreviewEditor/MarkDownPreviewEditor';
import styles from './ChallengeLearningOutcomeSection.module.scss';

export interface ChallengeLearningOutcomeSectionProps {
  validationHandler: (validateSection: () => Promise<boolean>) => void;
}

const CONTENT_TEMPLATE = `### Introduction

### Topics Covered

### Technical knowledge prerequisites

`;

const ChallengeLearningOutcomeSection: React.FC<ChallengeLearningOutcomeSectionProps> = ({ validationHandler }) => {
  const field = i18nKeys.challenges.subSections.learningPlan.fields;
  const { t } = useTranslation();
  const { handleUpdateChallengeProp, editedChallenge, challengeLearningOutcomeValidator } = useCreateChallenge();
  const [learningOutcome, setLearningOutcome] = useState<ChallengeLearningOutcome>(
    editedChallenge?.props.learningOutcome || new ChallengeLearningOutcome()
  );

  const handleLearningOutcome = (modifiedLearningOutcome: ChallengeLearningOutcome) => {
    setLearningOutcome(modifiedLearningOutcome);
    handleUpdateChallengeProp(ChallengePropAction.LEARNING_OUTCOME, modifiedLearningOutcome);
  };

  // error texts and validation logic
  const [summaryErrorText, setSummaryErrorText] = useState<string>('');
  const [contentErrorText, setContentErrorText] = useState<string>('');

  const validator = challengeLearningOutcomeValidator(
    new Map<ChallengeLearningOutcomeFields, (error: string) => void>([
      [ChallengeLearningOutcomeFields.SUMMARY, (error: string) => setSummaryErrorText(error)],
      [ChallengeLearningOutcomeFields.CONTENT, (error: string) => setContentErrorText(error)],
    ])
  );

  interface LearningOutcomeHelper {
    attribute: ChallengeLearningOutcomeFields;
    value: string;
  }

  const handleInputChange = (event: LearningOutcomeHelper) => {
    const { attribute, value } = event;
    switch (attribute) {
      case ChallengeLearningOutcomeFields.SUMMARY:
        learningOutcome.summary = value;
        break;
      case ChallengeLearningOutcomeFields.CONTENT:
        learningOutcome.content = value;
        break;
      default:
        throw new Error('Unrecognized field on ChallengeLearningOutcome');
    }
    handleLearningOutcome(learningOutcome);
  };

  validationHandler(() => {
    return Promise.resolve(validator.isValidSection(true));
  });

  useEffect(() => {
    if (!learningOutcome?.content) {
      handleInputChange({ attribute: ChallengeLearningOutcomeFields.CONTENT, value: CONTENT_TEMPLATE });
    }
  }, []);

  const contentConstraintProps = useMemo(() => {
    const count = (learningOutcome?.content || '').length;
    const msg = `${t(i18nKeys.challenges.subSections.learningPlan.fields.content.constraint, {
      count,
      maxLength: LEARNING_OUTCOME_CONTENT_MAX_LENGTH,
    })}. ${t(i18nKeys.general.avoidPIIWarning)}`;
    if (count > LEARNING_OUTCOME_CONTENT_MAX_LENGTH) {
      return { errorText: msg };
    }
    return { constraintText: msg };
  }, [learningOutcome?.content]);

  return (
    <Container header={<Header variant={'h2'}> Content </Header>}>
      <SpaceBetween direction="vertical" size="l">
        <FormField
          label={t(field.summary.title) + '*'}
          errorText={summaryErrorText}
          i18nStrings={{ errorIconAriaLabel: t(i18nKeys.general.error) }}
          constraintText={`${t(field.summary.constraint, { SUMMARY_MIN_LENGTH, SUMMARY_MAX_LENGTH })} ${t(i18nKeys.general.avoidPIIWarning)}`}
        >
          <Hotspot direction="right" hotspotId={ChallengeHotspot.learningOutcomeSummary}>
            <Textarea
              data-testid="challengeSummaryInput"
              onChange={({ detail }) =>
                handleInputChange({
                  attribute: ChallengeLearningOutcomeFields.SUMMARY,
                  value: detail.value,
                })
              }
              onBlur={() => validator.isValidField(ChallengeLearningOutcomeFields.SUMMARY)}
              name="learningOutcomeSummary"
              placeholder={t(field.summary.placeholder)}
              value={learningOutcome.summary ?? ''}
            />
          </Hotspot>
        </FormField>
        <FormField
          label={t(i18nKeys.challenges.subSections.learningPlan.fields.content.title)}
          stretch
          {...contentConstraintProps}
        >
          <Grid gridDefinition={[{ colspan: 11 }, { colspan: 1 }]}>
            <MarkDownPreviewEditor
              text={learningOutcome.content || CONTENT_TEMPLATE}
              onTextChanged={(text) => {
                handleInputChange({ attribute: ChallengeLearningOutcomeFields.CONTENT, value: text.trim() });
                validator.isValidField(ChallengeLearningOutcomeFields.CONTENT);
              }}
            />

            <div className={styles['hotspot-anchor']}>
              <Hotspot direction="left" side="left" hotspotId={ChallengeHotspot.learningOutcomeIntroduction}>
                <Hotspot direction="left" side="left" hotspotId={ChallengeHotspot.learningOutcomeTopicsCovered}>
                  <Hotspot
                    direction="left"
                    side="left"
                    hotspotId={ChallengeHotspot.learningOutcomeTechnicalKnowledgePrerequisites}
                  >
                    <div> </div>
                  </Hotspot>
                </Hotspot>
              </Hotspot>
            </div>
          </Grid>
        </FormField>
      </SpaceBetween>
    </Container>
  );
};
export default ChallengeLearningOutcomeSection;
